import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';

import ActivityBlockHeader from '@adsk/bim360-matrix-react-activity-block-header';
import ActivityLine from './ActivityLine';
import LongTextActivityLine from './LongTextActivityLine';
import ActivityAttachmentsBlock from './ActivityAttachmentsBlock';

import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { useTheme } from '@adsk/bim360-matrix-react-helpers/es/theme';

import './_ActivityBlock.scss';

/**
 * Enumerator containing the activity SubComponents:
 * DEFAULT: The default Key/Value SubComponent (can be used to rendered custom nodes as fieldValue).
 * LONG_TEXT: Shows only the key (field's name) and a "Show/Hide" button that controls the value's visibility..
 */
export const ActivitySubComponents = {
  DEFAULT: ActivityLine,
  LONG_TEXT: LongTextActivityLine,
  ATTACHMENTS_BLOCK: ActivityAttachmentsBlock
};

const ActivitySubComponentsValues = objectValues(ActivitySubComponents);
/**
 * ActivityBlock - An activity block that each item is rendered with a stylized bullet-list
 */
const ActivityBlock = props => {
  const { theme } = useTheme();
  /**
   * Renders the activity's items with the needed component
   */
  const _renderItems = () => {
    return props.items.map((item, index) => {
      const classes = classNames('ActivityBlock__item', item.props && item.props.className, {
        'ActivityBlock__item--last-item': index === props.items.length - 1,
        'ActivityBlock__item--no-bullet-line': item.hideBulletLine || item.alignLeft,
        'ActivityBlock__item--align-left': item.alignLeft
      });

      const Component = item.component || ActivitySubComponents.DEFAULT;
      if (!ActivitySubComponentsValues.includes(Component)) {
        return <Component key={index} {...item.props} className={classes} />;
      }

      return (
        <Component
          key={index}
          className={classes}
          fieldName={item.fieldName}
          fieldValue={item.fieldValue}
        />
      );
    });
  };

  const _renderHeader = classes => {
    const Header = props.headerComponent;

    return (
      <Header
        className={classes}
        user={props.user}
        activityTime={props.activityTime}
        container={props.container}
      />
    );
  };

  const noBulletLine = props.items.length === 1 && props.items[0].alignLeft;
  const headerClasses = classNames('ActivityBlock__header', {
    'ActivityBlock__header--no-bullet-line': noBulletLine
  });

  return (
    <div className={classNames('ActivityBlock', `ActivityBlock--${theme}`)}>
      {_renderHeader(headerClasses)}
      {_renderItems()}
    </div>
  );
};

ActivityBlock.defaultProps = {
  headerComponent: ActivityBlockHeader
};

ActivityBlock.propTypes = {
  ...ActivityBlockHeader.propTypes,
  /**
   * User as specified in UserAvatar
   */
  user: PropTypes.object,
  /**
   * A moment object or date of the activity's time
   */
  activityTime: PropTypes.oneOfType([
    PropTypes.instanceOf(moment),
    PropTypes.instanceOf(Date),
    PropTypes.string
  ]),
  /**
   * The component to render as the ActivityBlock header (receives className, user, activityTime and container as props).
   */
  headerComponent: PropTypes.func,
  /**
   * An array of activity item objects to render within the activity block.
   * Each activity item object should contain the following:
   * fieldName: string (required)
   * fieldValue: A string or a React Node (required) that will be rendered on the right side of the fieldName
   * component: An optional Activity SubComponent to use for this item supplied from the exported ActivitySubComponents. When not supplied defaults to ActivitySubComponents.DEFAULT
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The name of the field that was changed, or a phrasing to indicate the change
       */
      fieldName: PropTypes.string,
      /**
       * Required unless you pass a component which doesn't need this
       */
      fieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      /**
       * Optional - A component to render if you don't want the default one. Either one out of ActivitySubComponents, or a custom one of your choice
       */
      component: PropTypes.oneOfType([
        PropTypes.oneOf(ActivitySubComponentsValues),
        PropTypes.func
      ]),
      /**
       * Props for the component
       */
      props: PropTypes.object,
      /**
       * Adds "ActivityBlock__item--no-bullet-line" class when passed true. You can then add your class in CSS rule in ActivityBlock.scss that overrides the bullet line
       */
      hideBulletLine: PropTypes.bool,
      /**
       * If passed true, will align the component to the Bullets line
       */
      alignLeft: PropTypes.bool
    })
  ).isRequired,
  /**
   * Container to pass to ActivityBlockHeader for tooltip
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

export default ActivityBlock;
